/**
 * M15CustomerListScreen - Quản lý khách hàng
 * Matches Curex 4_create_customer/image_3.png layout
 *
 * Features:
 * - 3 stats cards (Tổng KH / KH mới tháng / Sinh nhật tháng)
 * - Search + 2 filter dropdowns + "Thêm khách hàng" button
 * - Table with 8 columns + 3-dot menu per row
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '../types/navigation';
import { useSetAtom } from 'jotai';
import { selectedCustomerAtom } from './customerAtoms';
import { illustrationInputAtom } from './illustrationAtoms';
import { Icon } from '../components/Icon';
import { EditModal } from '../components/EditModal';
import { ConfirmDeleteModal } from '../components/ConfirmDeleteModal';
import { QuickNoteModal } from '../components/QuickNoteModal';
import { buildCustomerEditFields } from '../data/customers';
import { colors, spacing, fontWeights, borderRadius } from '../theme';
import { customerRepository } from '../repositories/CustomerRepository';

type NavigationProp = StackNavigationProp<RootStackParamList, 'M15CustomerListScreen'>;

export type CustomerCategory = 'main' | 'dependent';
export type CustomerLabel = 'vip' | 'potential' | 'loyal' | 'new' | 'family' | 'business' | 'consulting';

export interface Customer {
  id: string;
  name: string;
  category: CustomerCategory;
  dependentsCount: number;
  dob: string;
  age: number;
  gender: 'Nam' | 'Nữ';
  phone: string;
  email: string;
  labels: CustomerLabel[];
  occupation: string;
  createdAt: string;
  updatedAt?: string;
  created_at?: string;
  updated_at?: string;
  isLocal?: boolean;
}

export const LABEL_META: Record<CustomerLabel, { label: string; color: string; bg: string }> = {
  vip: { label: 'VIP', color: '#92400E', bg: '#FEF3C7' },
  potential: { label: 'Tiềm năng', color: '#1E40AF', bg: '#DBEAFE' },
  loyal: { label: 'Thân thiết', color: '#065F46', bg: '#D1FAE5' },
  new: { label: 'Khách mới', color: '#3730A3', bg: '#E0E7FF' },
  family: { label: 'Gia đình', color: '#831843', bg: '#FCE7F3' },
  business: { label: 'Doanh nghiệp', color: '#7C2D12', bg: '#FED7AA' },
  consulting: { label: 'Đang tư vấn', color: '#92400E', bg: '#FEF3C7' },
};

export const M15CustomerListScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const setSelectedCustomer = useSetAtom(selectedCustomerAtom);
  const setIllustrationInput = useSetAtom(illustrationInputAtom);

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<'all' | 'main' | 'dependent'>('all');
  const [filterBirthday, setFilterBirthday] = useState<'all' | 'thisMonth' | 'nextMonth'>('all');
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [notingCustomer, setNotingCustomer] = useState<Customer | null>(null);
  const [customerList, setCustomerList] = useState<Customer[]>([]);

  const reloadCustomers = useCallback(async () => {
    const list = await customerRepository.getAll();
    setCustomerList(list);
  }, []);

  useFocusEffect(
    useCallback(() => {
      reloadCustomers();
    }, [reloadCustomers])
  );

  const filtered = customerList.filter((c) => {
    const matchesSearch = c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.phone.includes(search) || c.dob.includes(search);
    const matchesCategory = filterCategory === 'all' || c.category === filterCategory;
    let matchesBirthday = true;
    if (filterBirthday === 'thisMonth') {
      const now = new Date();
      matchesBirthday = c.dob.split('/')[1] === String(now.getMonth() + 1).padStart(2, '0');
    }
    return matchesSearch && matchesCategory && matchesBirthday;
  });

  // Stats (current month)
  const now = new Date();
  const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
  const newThisMonth = customerList.length;
  const birthdaysThisMonth = customerList.filter((c) => c.dob.split('/')[1] === currentMonth).length;

  const handleAction = (action: string, customer: Customer) => {
    switch (action) {
      case 'newIllustration':
        // Update Jotai atoms for global state management
        setSelectedCustomer(customer);
        setIllustrationInput((prev) => ({
          ...prev,
          customerName: customer.name,
          customerAge: customer.age,
          customerGender: customer.gender,
          customerDob: customer.dob,
          customerPhone: customer.phone,
          customerEmail: customer.email,
        }));
        // Navigate cleanly without requiring long parameter strings in route
        navigation.navigate('M06InputCustomerScreen');
        break;

      case 'edit':
        setEditingCustomer({ ...customer });
        break;

      case 'note':
        setNotingCustomer(customer);
        break;

      case 'delete':
        setDeletingCustomer(customer);
        break;

      case 'consult':
        Alert.alert('Trang tư vấn viên', `Mở trang tư vấn cho ${customer.name}`);
        break;

      case 'library':
        Alert.alert('Thư viện giải pháp', `Mở thư viện cho ${customer.name}`);
        break;
    }
  };

  const showActionMenu = (customerId: string) => {
    const customer = customerList.find((c: Customer) => c.id === customerId);
    if (!customer) return;

    const actionButtons: any[] = [
      { text: '➕ Tạo minh họa mới', onPress: () => handleAction('newIllustration', customer) },
      { text: '✏️ Chỉnh sửa thông tin', onPress: () => handleAction('edit', customer) },
      { text: '📝 Ghi chú khách hàng', onPress: () => handleAction('note', customer) },
      { text: '🗑️ Xóa khách hàng', style: 'destructive', onPress: () => handleAction('delete', customer) },
      { text: 'Hủy', style: 'cancel' },
    ];

    Alert.alert(customer.name, 'Chọn hành động:', actionButtons);
  };

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Header */}
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text style={styles.breadcrumb}>Quản lý khách hàng</Text>
            <Text style={styles.title}>Quản lý khách hàng</Text>
            <Text style={styles.subtitle}>Danh sách Bên mua bảo hiểm và Người phụ thuộc</Text>
          </View>
          <View style={styles.advisorBadge}>
            <Text style={styles.advisorName}>NGUYỄN VĂN CAO</Text>
            <Text style={styles.advisorRole}>FA - Hotline: 0363077359</Text>
          </View>
        </View>

        {/* Stats Cards — hidden
        <View style={styles.statsRow}>
          <View style={[styles.statCard, { borderLeftColor: colors.primary }]}>
            <View style={[styles.statIcon, { backgroundColor: '#EFF6FF' }]}>
              <Icon name="person" size={18} color={colors.primary} />
            </View>
            <Text style={styles.statLabel}>Tổng số khách hàng</Text>
            <Text style={styles.statValue}>{CUSTOMERS.length} người</Text>
          </View>

          <View style={[styles.statCard, { borderLeftColor: '#10B981' }]}>
            <View style={[styles.statIcon, { backgroundColor: '#D1FAE5' }]}>
              <Icon name="person" size={18} color="#10B981" />
            </View>
            <Text style={styles.statLabel}>Khách hàng mới trong tháng</Text>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: spacing[1] }}>
              <Text style={styles.statValue}>+{newThisMonth} người</Text>
              <Text style={styles.statSub}>↘ 100% Tháng này</Text>
            </View>
          </View>

          <View style={[styles.statCard, { borderLeftColor: '#F59E0B' }]}>
            <View style={[styles.statIcon, { backgroundColor: '#FEF3C7' }]}>
              <Icon name="history" size={18} color="#F59E0B" />
            </View>
            <Text style={styles.statLabel}>Nhắc sinh nhật trong tháng</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={styles.statValue}>{birthdaysThisMonth} người</Text>
              <Icon name="chevron_right" size={16} color={colors.onSurfaceVariant} />
            </View>
          </View>
        </View>
        */}

        {/* Filters */}
        <View style={styles.filterCard}>
          <View style={styles.filterRow}>
            <View style={styles.searchWrapper}>
              <Icon name="search" size={18} color={colors.onSurfaceVariant} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Tìm kiếm theo tên, ngày sinh, sđt"
                value={search}
                onChangeText={setSearch}
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Icon name="close" size={18} color={colors.onSurfaceVariant} />
                </TouchableOpacity>
              )}
            </View>

            <TouchableOpacity style={styles.filterDropdown}>
              <Icon name="person" size={16} color={colors.onSurfaceVariant} />
              <Text style={styles.filterDropdownText}>
                {filterCategory === 'all' ? 'Tất cả' : filterCategory === 'main' ? 'Bên mua BH' : 'Người phụ thuộc'}
              </Text>
              <Icon name="chevron_right" size={16} color={colors.onSurfaceVariant} />
            </TouchableOpacity>

            <TouchableOpacity style={styles.filterDropdown}>
              <Icon name="calendar_today" size={16} color={colors.onSurfaceVariant} />
              <Text style={styles.filterDropdownText}>
                {filterBirthday === 'all' ? 'Sinh nhật' : filterBirthday === 'thisMonth' ? 'Tháng này' : 'Tháng sau'}
              </Text>
              <Icon name="chevron_right" size={16} color={colors.onSurfaceVariant} />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.addBtn}
              onPress={() => Alert.alert('Thêm khách hàng', 'Mở form tạo hồ sơ khách hàng mới')}
            >
              <Text style={styles.addBtnText}>+ Thêm khách hàng</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Scroll helper */}
        <Text style={styles.scrollHelperText}>Vui lòng kéo sang phải để xem đầy đủ thông tin danh sách.</Text>

        {/* Table */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator
          style={styles.tableScroll}
          contentContainerStyle={styles.tableScrollContent}
        >
          <View style={styles.table}>
            {/* Header */}
            <View style={styles.tableHeader}>
              <Text style={[styles.headerCell, styles.colStt]}>STT</Text>
              <Text style={[styles.headerCell, styles.colName]}>HỌ & TÊN KHÁCH HÀNG</Text>
              <Text style={[styles.headerCell, styles.colCategory]}>PHÂN LOẠI</Text>
              <Text style={[styles.headerCell, styles.colDep]}>NGƯỜI PHỤ THUỘC</Text>
              <Text style={[styles.headerCell, styles.colBasic]}>TT CƠ BẢN</Text>
              <Text style={[styles.headerCell, styles.colContact]}>TT LIÊN HỆ</Text>
              <Text style={[styles.headerCell, styles.colNote]}>GHI CHÚ</Text>
              <Text style={[styles.headerCell, styles.colAction]}>THAO TÁC</Text>
            </View>

            {/* Rows */}
            {filtered.map((c, idx) => (
              <View key={c.id} style={[styles.tableRow, idx % 2 === 1 && { backgroundColor: '#f9fafb' }]}>
                <View style={[styles.cell, styles.colStt]}><Text style={styles.cellText}>{idx + 1}</Text></View>

                <View style={[styles.cell, styles.colName]}>
                  <TouchableOpacity
                    style={styles.avatarCol}
                    onPress={() => navigation.navigate('M16CustomerDetailScreen', { customerId: c.id })}
                  >
                    <View style={[styles.avatar, { backgroundColor: c.gender === 'Nữ' ? '#EC4899' : '#10B981' }]}>
                      <Text style={styles.avatarText}>{c.name.charAt(0)}</Text>
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.customerName}>{c.name}</Text>
                      <Text style={styles.customerOcc}>{c.occupation}</Text>
                    </View>
                  </TouchableOpacity>
                </View>

                <View style={[styles.cell, styles.colCategory]}>
                  <Text style={[styles.categoryTag, { color: c.category === 'main' ? '#065F46' : '#7C2D12', backgroundColor: c.category === 'main' ? '#D1FAE5' : '#FED7AA' }]}>
                    {c.category === 'main' ? 'Bên mua bảo hiểm' : 'Người phụ thuộc'}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colDep]}>
                  <View style={[styles.dependentsBadge, c.dependentsCount === 0 && styles.dependentsBadgeZero]}>
                    <Text style={[styles.dependentsBadgeText, c.dependentsCount === 0 && { color: '#9CA3AF' }]}>{c.dependentsCount}</Text>
                  </View>
                </View>

                <View style={[styles.cell, styles.colBasic]}>
                  <Text style={styles.cellTextDark}>{c.dob}</Text>
                  <Text style={styles.cellTextSub}>{c.age} tuổi - {c.gender}</Text>
                </View>

                <View style={[styles.cell, styles.colContact]}>
                  <Text style={styles.cellTextDark}>{c.phone}</Text>
                  <Text style={styles.cellTextSub} numberOfLines={1}>{c.email}</Text>
                </View>

                <View style={[styles.cell, styles.colNote]}>
                  <View style={{ flexDirection: 'row', gap: spacing[1], flexWrap: 'wrap' }}>
                    {c.labels.map((lbl) => (
                      <View
                        key={lbl}
                        style={[styles.labelTag, { backgroundColor: LABEL_META[lbl].bg }]}
                      >
                        <Text style={[styles.labelTagText, { color: LABEL_META[lbl].color }]}>
                          ⭐ {LABEL_META[lbl].label}
                        </Text>
                      </View>
                    ))}
                  </View>
                </View>

                <View style={[styles.cell, styles.colAction]}>
                  <TouchableOpacity
                    style={[styles.actionIconBtn, { backgroundColor: '#F0FDF4' }]}
                    onPress={() => handleAction('newIllustration', c)}
                    accessibilityLabel="Tạo minh họa"
                  >
                    <Icon name="add" size={16} color="#00A651" />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionIconBtn, { backgroundColor: '#EFF6FF' }]}
                    onPress={() => handleAction('edit', c)}
                    accessibilityLabel="Chỉnh sửa"
                  >
                    <Icon name="edit" size={16} color="#2563EB" />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionIconBtn, { backgroundColor: '#FEF3C7' }]}
                    onPress={() => handleAction('note', c)}
                    accessibilityLabel="Ghi chú"
                  >
                    <Icon name="comment" size={16} color="#D97706" />
                  </TouchableOpacity>

                  <TouchableOpacity
                    style={[styles.actionIconBtn, { backgroundColor: '#FEE2E2' }]}
                    onPress={() => handleAction('delete', c)}
                    accessibilityLabel="Xóa"
                  >
                    <Icon name="delete" size={16} color="#DC2626" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            {filtered.length === 0 && (
              <View style={styles.emptyContainer}>
                <Icon name="person" size={48} color={colors.onSurfaceVariant} />
                <Text style={styles.emptyText}>Chưa có khách hàng nào khớp với bộ lọc</Text>
              </View>
            )}
          </View>
        </ScrollView>

        {/* Footer */}
        <View style={styles.footerNote}>
          <Text style={styles.footerNoteText}>
            💡 Thao tác nhanh: ➕ Tạo minh họa | ✏️ Sửa | 📝 Ghi chú | 🗑️ Xóa (có Popup confirm)
          </Text>
        </View>
      </ScrollView>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <EditModal
          visible
          title={`Chỉnh sửa: ${editingCustomer.name}`}
          subtitle="Cập nhật thông tin khách hàng"
          fields={buildCustomerEditFields(editingCustomer, setEditingCustomer)}
          onCancel={() => setEditingCustomer(null)}
          onSave={async () => {
            await customerRepository.update(editingCustomer.id, editingCustomer);
            await reloadCustomers();
            Alert.alert('Thành công', `Đã cập nhật thông tin ${editingCustomer.name}`);
            setEditingCustomer(null);
          }}
        />
      )}

      {/* Confirm Delete Modal */}
      {deletingCustomer && (
        <ConfirmDeleteModal
          visible={!!deletingCustomer}
          title="Xóa khách hàng"
          customerName={deletingCustomer.name}
          message={`Bạn có chắc chắn muốn xóa hồ sơ khách hàng "${deletingCustomer.name}"? Hành động này sẽ xóa dữ liệu khỏi hệ thống.`}
          confirmText="Xóa khách hàng"
          onClose={() => setDeletingCustomer(null)}
          onConfirm={async () => {
            await customerRepository.delete(deletingCustomer.id);
            await reloadCustomers();
            setDeletingCustomer(null);
            Alert.alert('Thành công', 'Đã xóa khách hàng.');
          }}
        />
      )}

      {/* Quick Note Modal */}
      {notingCustomer && (
        <QuickNoteModal
          visible={!!notingCustomer}
          customerId={notingCustomer.id}
          customerName={notingCustomer.name}
          onClose={() => setNotingCustomer(null)}
          onSaved={() => reloadCustomers()}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  scrollContent: {
    padding: spacing[5],
    paddingBottom: 88,
    gap: spacing[4],
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing[3],
    marginBottom: spacing[2],
  },
  breadcrumb: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  title: {
    fontSize: 24,
    fontWeight: fontWeights.bold,
    color: '#00A651',
    marginTop: spacing[1],
  },
  subtitle: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  advisorBadge: {
    backgroundColor: '#ffffff',
    paddingHorizontal: spacing[3],
    paddingVertical: spacing[2],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    alignItems: 'flex-end',
    minWidth: 220,
  },
  advisorName: {
    fontSize: 12,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    letterSpacing: 0.5,
  },
  advisorRole: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },

  /* Stats Cards */
  statsRow: {
    flexDirection: 'row',
    gap: spacing[3],
  },
  statCard: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderLeftWidth: 4,
    gap: spacing[1],
  },
  statIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  statLabel: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
  statValue: {
    fontSize: 18,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
  },
  statSub: {
    fontSize: 11,
    color: '#10B981',
    fontWeight: fontWeights.medium,
  },

  /* Filters */
  filterCard: {
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.md,
    padding: spacing[3],
    borderWidth: 1,
    borderColor: colors.outlineVariant,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
    flexWrap: 'wrap',
  },
  searchWrapper: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    paddingHorizontal: spacing[3],
    backgroundColor: '#F9FAFB',
    height: 40,
    minWidth: 200,
  },
  searchIcon: {
    marginRight: spacing[2],
  },
  searchInput: {
    flex: 1,
    height: '100%',
    color: colors.onSurface,
    fontSize: 13,
  },
  filterDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1.5],
    paddingHorizontal: spacing[3],
    height: 40,
    borderWidth: 1,
    borderColor: colors.outline,
    borderRadius: borderRadius.DEFAULT,
    backgroundColor: '#ffffff',
    minWidth: 130,
  },
  filterDropdownText: {
    flex: 1,
    fontSize: 13,
    color: colors.onSurface,
  },
  addBtn: {
    backgroundColor: '#7C3AED',
    paddingHorizontal: spacing[4],
    height: 40,
    borderRadius: borderRadius.DEFAULT,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  addBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: fontWeights.bold,
  },
  scrollHelperText: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    fontStyle: 'italic',
  },

  /* Table */
  tableScroll: {
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    borderRadius: borderRadius.md,
    backgroundColor: '#ffffff',
    width: '100%',
  },
  tableScrollContent: {
    minWidth: '100%',
  },
  table: {
    width: '100%',
    minWidth: 1070,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F9FAFB',
    borderBottomWidth: 1,
    borderBottomColor: colors.outlineVariant,
    paddingVertical: spacing[3],
    width: '100%',
  },
  headerCell: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
    color: colors.onSurface,
    paddingHorizontal: spacing[3],
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#f3f4f6',
    paddingVertical: spacing[4],
    width: '100%',
  },
  cell: {
    paddingHorizontal: spacing[3],
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 13,
    color: colors.onSurface,
  },
  cellTextDark: {
    fontSize: 13,
    fontWeight: fontWeights.medium,
    color: colors.onSurface,
  },
  cellTextSub: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },
  colStt: { minWidth: 50, flex: 0.5 },
  colName: { minWidth: 200, flex: 2 },
  colCategory: { minWidth: 150, flex: 1.5 },
  colDep: { minWidth: 140, flex: 1.4 },
  colBasic: { minWidth: 130, flex: 1.2 },
  colContact: { minWidth: 180, flex: 1.8 },
  colNote: { minWidth: 180, flex: 1.8 },
  colAction: { minWidth: 160, flex: 1.6, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 },
  actionIconBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },

  /* Avatar + customer name */
  avatarCol: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[2],
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 14,
    fontWeight: fontWeights.bold,
    color: '#ffffff',
  },
  customerName: {
    fontSize: 13,
    fontWeight: fontWeights.semibold,
    color: colors.onSurface,
  },
  customerOcc: {
    fontSize: 11,
    color: colors.onSurfaceVariant,
    marginTop: 2,
  },

  /* Category tag */
  categoryTag: {
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
    fontSize: 11,
    fontWeight: fontWeights.semibold,
    alignSelf: 'flex-start',
  },

  /* Dependents badge */
  dependentsBadge: {
    width: 32,
    height: 22,
    borderRadius: 11,
    borderWidth: 1,
    borderColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  dependentsBadgeZero: {
    borderColor: '#D1D5DB',
  },
  dependentsBadgeText: {
    fontSize: 11,
    color: colors.primary,
    fontWeight: fontWeights.semibold,
  },

  /* Label tag (ghi chú) */
  labelTag: {
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: borderRadius.full,
  },
  labelTagText: {
    fontSize: 10,
    fontWeight: fontWeights.semibold,
  },

  /* Menu button */
  menuBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
  },

  /* Empty state */
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing[10],
    gap: spacing[3],
  },
  emptyText: {
    fontSize: 13,
    color: colors.onSurfaceVariant,
  },

  /* Footer */
  footerNote: {
    marginTop: spacing[2],
    paddingHorizontal: spacing[2],
  },
  footerNoteText: {
    fontSize: 12,
    color: colors.onSurfaceVariant,
  },
});